// Campaign Data Models
import { Timestamp } from 'firebase/firestore';

// Campaign Types
export const CAMPAIGN_TYPES = {
  PROMOTION: 'promotion', // Free Trial, Discount, Special Offers
  BANNER_RENTAL: 'banner-rental', // Paid Advertising
  SEASONAL: 'seasonal', // Christmas, New Year, etc.
  ANNOUNCEMENT: 'announcement' // System Announcements
};

// Campaign Status
export const CAMPAIGN_STATUS = {
  DRAFT: 'draft',
  PENDING_APPROVAL: 'pending-approval',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  ACTIVE: 'active',
  PAUSED: 'paused',
  EXPIRED: 'expired'
};

// Approval Status
export const APPROVAL_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected'
};

// Moderation Status
export const MODERATION_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected'
};

// Payment Methods (Phase 1: T Coins only)
export const PAYMENT_METHODS = {
  BONUS_COINS: 'bonus-coins',
  T_COINS: 't-coins'
  // Future: BANK, CREDIT_CARD, etc.
};

// Payment Status
export const PAYMENT_STATUS = {
  PENDING: 'pending',
  PAID: 'paid',
  REFUNDED: 'refunded'
};

// Pricing Types
export const PRICING_TYPES = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly'
};

// Campaign Positions (Future: Multiple positions)
export const CAMPAIGN_POSITIONS = {
  HOMEPAGE_TOP: 'homepage-top',
  HOMEPAGE_MIDDLE: 'homepage-middle',
  NURSE_HOMEPAGE_TOP: 'nurse-homepage-top', // NurseApp homepage banner
  PRODUCT_LIST_MIDDLE: 'product-list-middle', // Future
  PRODUCT_DETAIL_SIDEBAR: 'product-detail-sidebar' // Future
};

// Position Display Types
export const DISPLAY_TYPES = {
  CAROUSEL: 'carousel',
  STACK: 'stack',
  INLINE: 'inline'
};

/**
 * Create Campaign Model
 */
export const createCampaignModel = ({
  name = {},
  type = CAMPAIGN_TYPES.PROMOTION,
  status = CAMPAIGN_STATUS.DRAFT,
  startDate = null,
  endDate = null,
  banner = {},
  features = {},
  rental = null,
  positions = [],
  targeting = {},
  createdBy = null,
  isActive = true
}) => {
  const now = Timestamp.now();
  
  return {
    // Basic Info
    name: {
      th: name.th || '',
      en: name.en || ''
    },
    type,
    status,
    
    // Approval & Moderation
    approval: {
      status: APPROVAL_STATUS.PENDING,
      reviewedBy: null,
      reviewedAt: null,
      rejectionReason: null
    },
    moderation: {
      status: MODERATION_STATUS.PENDING,
      reviewedBy: null,
      reviewedAt: null,
      flags: [],
      notes: null
    },
    
    // Dates
    startDate: startDate ? (startDate instanceof Timestamp ? startDate : Timestamp.fromDate(new Date(startDate))) : null,
    endDate: endDate ? (endDate instanceof Timestamp ? endDate : Timestamp.fromDate(new Date(endDate))) : null,
    
    // Banner Configuration (Multi-language)
    banner: {
      desktop: {
        th: {
          imageUrl: banner.desktop?.th?.imageUrl || '',
          linkUrl: banner.desktop?.th?.linkUrl || '',
          action: banner.desktop?.th?.action || 'open-modal' // 'open-modal', 'navigate', 'external-link'
        },
        en: {
          imageUrl: banner.desktop?.en?.imageUrl || '',
          linkUrl: banner.desktop?.en?.linkUrl || '',
          action: banner.desktop?.en?.action || 'open-modal'
        }
      },
      mobile: {
        th: {
          imageUrl: banner.mobile?.th?.imageUrl || '',
          linkUrl: banner.mobile?.th?.linkUrl || '',
          action: banner.mobile?.th?.action || 'open-modal'
        },
        en: {
          imageUrl: banner.mobile?.en?.imageUrl || '',
          linkUrl: banner.mobile?.en?.linkUrl || '',
          action: banner.mobile?.en?.action || 'open-modal'
        }
      }
    },
    
    // Campaign Features
    features: {
      bonusCoins: {
        enabled: features.bonusCoins?.enabled || false,
        amount: features.bonusCoins?.amount || 0,
        coinType: features.bonusCoins?.coinType || 'virtual',
        claimLimit: features.bonusCoins?.claimLimit || 1,
        claimOncePerUser: features.bonusCoins?.claimOncePerUser !== false
      },
      packageTrial: {
        enabled: features.packageTrial?.enabled || false,
        packageType: features.packageTrial?.packageType || 'premium',
        trialDays: features.packageTrial?.trialDays || 14
      },
      videoCallTrial: {
        enabled: features.videoCallTrial?.enabled || false,
        freeCalls: features.videoCallTrial?.freeCalls || 0,
        trialDays: features.videoCallTrial?.trialDays || 0,
        unlimitedDuration: features.videoCallTrial?.unlimitedDuration || false
      },
      storeBoost: {
        enabled: features.storeBoost?.enabled || false,
        maxProducts: features.storeBoost?.maxProducts || 0,
        maxCoverImages: features.storeBoost?.maxCoverImages || 0,
        maxProductImages: features.storeBoost?.maxProductImages || 0,
        maxBankAccounts: features.storeBoost?.maxBankAccounts || 0,
        duration: features.storeBoost?.duration || 0
      },
      featuredStore: {
        enabled: features.featuredStore?.enabled || false,
        duration: features.featuredStore?.duration || 0,
        positions: features.featuredStore?.positions || []
      },
      productPromotion: {
        enabled: features.productPromotion?.enabled || false,
        productCount: features.productPromotion?.productCount || 0,
        duration: features.productPromotion?.duration || 0,
        positions: features.productPromotion?.positions || []
      },
      auctionFree: {
        enabled: features.auctionFree?.enabled || false,
        auctionCount: features.auctionFree?.auctionCount || 0,
        duration: features.auctionFree?.duration || 0
      },
      buyerRequestFree: {
        enabled: features.buyerRequestFree?.enabled || false,
        requestCount: features.buyerRequestFree?.requestCount || 0,
        duration: features.buyerRequestFree?.duration || 0
      },
      analyticsAccess: {
        enabled: features.analyticsAccess?.enabled || false,
        duration: features.analyticsAccess?.duration || 0
      }
    },
    
    // Rental Info (สำหรับ banner-rental)
    rental: rental ? {
      customerId: rental.customerId || null,
      customerName: rental.customerName || '',
      customerEmail: rental.customerEmail || '',
      paymentMethod: rental.paymentMethod || PAYMENT_METHODS.T_COINS,
      paymentAmount: rental.paymentAmount || 0,
      paymentStatus: rental.paymentStatus || PAYMENT_STATUS.PENDING,
      paidAt: rental.paidAt ? (rental.paidAt instanceof Timestamp ? rental.paidAt : Timestamp.fromDate(new Date(rental.paidAt))) : null,
      transactionId: rental.transactionId || '',
      pricing: {
        type: rental.pricing?.type || PRICING_TYPES.DAILY,
        rate: rental.pricing?.rate || 0,
        totalDays: rental.pricing?.totalDays || 0,
        totalAmount: rental.pricing?.totalAmount || 0
      }
    } : null,
    
    // Positions & Priority
    positions: positions.length > 0 ? positions : [
      {
        positionId: CAMPAIGN_POSITIONS.HOMEPAGE_TOP,
        priority: 1,
        enabled: true
      }
    ],
    
    // Targeting
    targeting: {
      roles: targeting.roles || ['user', 'farm'],
      userTypes: targeting.userTypes || ['all'],
      languages: targeting.languages || ['th', 'en']
    },
    
    // Display Control
    isActive: true, // เปิด/ปิด การแสดง campaign
    
    // Analytics
    analytics: {
      views: 0,
      clicks: 0,
      conversions: 0,
      uniqueViews: 0,
      uniqueClicks: 0,
      dailyStats: []
    },
    
    // Metadata
    createdBy: createdBy || null,
    createdAt: now,
    updatedAt: now
  };
};

/**
 * Create Campaign Claim Model (สำหรับ Promotion Campaigns)
 */
export const createCampaignClaimModel = ({
  campaignId,
  userId,
  bonusCoinsReceived = 0,
  packageTrialActivated = false
}) => {
  return {
    campaignId,
    userId,
    bonusCoinsReceived,
    packageTrialActivated,
    claimedAt: Timestamp.now()
  };
};

/**
 * Get Campaign Type Display Name
 */
export const getCampaignTypeName = (type, language = 'th') => {
  const names = {
    [CAMPAIGN_TYPES.PROMOTION]: {
      th: 'โปรโมชัน',
      en: 'Promotion'
    },
    [CAMPAIGN_TYPES.BANNER_RENTAL]: {
      th: 'เช่า Banner',
      en: 'Banner Rental'
    },
    [CAMPAIGN_TYPES.SEASONAL]: {
      th: 'แคมเปญตามฤดูกาล',
      en: 'Seasonal Campaign'
    },
    [CAMPAIGN_TYPES.ANNOUNCEMENT]: {
      th: 'ประกาศ',
      en: 'Announcement'
    }
  };
  
  return names[type]?.[language] || type;
};

/**
 * Get Campaign Status Display Name
 */
export const getCampaignStatusName = (status, language = 'th') => {
  const names = {
    [CAMPAIGN_STATUS.DRAFT]: {
      th: 'ร่าง',
      en: 'Draft'
    },
    [CAMPAIGN_STATUS.PENDING_APPROVAL]: {
      th: 'รออนุมัติ',
      en: 'Pending Approval'
    },
    [CAMPAIGN_STATUS.APPROVED]: {
      th: 'อนุมัติแล้ว',
      en: 'Approved'
    },
    [CAMPAIGN_STATUS.REJECTED]: {
      th: 'ปฏิเสธ',
      en: 'Rejected'
    },
    [CAMPAIGN_STATUS.ACTIVE]: {
      th: 'ใช้งาน',
      en: 'Active'
    },
    [CAMPAIGN_STATUS.PAUSED]: {
      th: 'หยุดชั่วคราว',
      en: 'Paused'
    },
    [CAMPAIGN_STATUS.EXPIRED]: {
      th: 'หมดอายุ',
      en: 'Expired'
    }
  };
  
  return names[status]?.[language] || status;
};

/**
 * Check if Campaign is Active
 */
export const isCampaignActive = (campaign) => {
  if (!campaign) return false;
  if (campaign.status !== CAMPAIGN_STATUS.ACTIVE) return false;
  
  const now = new Date();
  const startDate = campaign.startDate?.toDate?.() || new Date(campaign.startDate);
  const endDate = campaign.endDate?.toDate?.() || new Date(campaign.endDate);
  
  if (startDate && now < startDate) return false;
  if (endDate && now > endDate) return false;
  
  return true;
};

/**
 * Check if Campaign is Expired
 */
export const isCampaignExpired = (campaign) => {
  if (!campaign) return true;
  if (campaign.status === CAMPAIGN_STATUS.EXPIRED) return true;
  
  const now = new Date();
  const endDate = campaign.endDate?.toDate?.() || new Date(campaign.endDate);
  
  if (endDate && now > endDate) return true;
  
  return false;
};

/**
 * Get Days Remaining for Campaign
 */
export const getCampaignDaysRemaining = (campaign) => {
  if (!campaign || !campaign.endDate) return null;
  
  const now = new Date();
  const endDate = campaign.endDate?.toDate?.() || new Date(campaign.endDate);
  
  if (now > endDate) return 0;
  
  const diffTime = endDate - now;
  const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return daysRemaining;
};

export default {
  CAMPAIGN_TYPES,
  CAMPAIGN_STATUS,
  APPROVAL_STATUS,
  MODERATION_STATUS,
  PAYMENT_METHODS,
  PAYMENT_STATUS,
  PRICING_TYPES,
  CAMPAIGN_POSITIONS,
  DISPLAY_TYPES,
  createCampaignModel,
  createCampaignClaimModel,
  getCampaignTypeName,
  getCampaignStatusName,
  isCampaignActive,
  isCampaignExpired,
  getCampaignDaysRemaining
};

